import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, MapPin, Briefcase, Calendar, DollarSign, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import usePageMeta from "../../lib/usePageMeta";

const humanize = (v) => (v ? String(v).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");

export default function CareerDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/careers/${slug}`)
      .then((r) => setJob(r.data))
      .catch((err) => {
        if (err.response?.status === 404) setNotFound(true);
        else toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      });
  }, [slug]);

  usePageMeta({
    title: job ? `${job.title} | California Corporate Chaplains` : "Careers | California Corporate Chaplains",
    description: job?.summary,
  });

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center" data-testid="career-not-found">
        <p className="font-heading text-2xl text-slate-900">This position is no longer accepting applications.</p>
        <Link to="/careers" className="mt-4 inline-flex items-center gap-2 text-[#4A6FA5] font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to all opportunities
        </Link>
      </div>
    );
  }
  if (!job) return <div className="py-24 text-center text-slate-500 text-sm">Loading…</div>;

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20" data-testid={`career-detail-${job.slug}`}>
      <Link to="/careers" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-8">
        <ArrowLeft className="w-4 h-4" /> All opportunities
      </Link>
      {job.department && <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-2">{job.department}</p>}
      {job.is_upcoming && (
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="upcoming-banner">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-[11px] uppercase tracking-[0.16em] font-semibold">
            Upcoming Position · Accepting Applications
          </span>
        </div>
      )}
      <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">{job.title}</h1>
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
        <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-slate-400" /> {job.location}</span>
        <span className="inline-flex items-center gap-1.5"><Briefcase className="w-4 h-4 text-slate-400" /> {humanize(job.employment_type)} · {humanize(job.work_arrangement)}</span>
        {job.schedule && <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-slate-400" /> {job.schedule}</span>}
        {job.compensation_range && <span className="inline-flex items-center gap-1.5"><DollarSign className="w-4 h-4 text-slate-400" /> {job.compensation_range}</span>}
      </div>
      {job.application_deadline && (
        <p className="mt-3 text-sm text-slate-500">Applications accepted through {job.application_deadline}.</p>
      )}

      {job.is_upcoming && (
        <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-2xl">
          California Corporate Chaplains is currently accepting applications for this upcoming role. An immediate start date is not guaranteed.
        </p>
      )}

      <button
        onClick={() => navigate(`/careers/${job.slug}/apply`)}
        data-testid="apply-cta-top"
        className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
      >
        Apply for This Position <ArrowRight className="w-4 h-4" />
      </button>

      <section className="mt-12 space-y-8 text-slate-700 leading-relaxed">
        <div>
          <h2 className="font-heading text-xl text-slate-900 mb-3">About the role</h2>
          <p className="whitespace-pre-wrap">{job.description}</p>
        </div>
        {job.responsibilities?.length > 0 && (
          <div>
            <h2 className="font-heading text-xl text-slate-900 mb-3">Potential responsibilities</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              {job.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
        {job.qualifications?.length > 0 && (
          <div>
            <h2 className="font-heading text-xl text-slate-900 mb-3">Qualifications</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              {job.qualifications.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
        {job.preferred_qualifications?.length > 0 && (
          <div>
            <h2 className="font-heading text-xl text-slate-900 mb-3">Preferred qualifications</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              {job.preferred_qualifications.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}
      </section>

      <div className="mt-12 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700 leading-relaxed">
          Please do not include Social Security numbers, medical information, financial information, or other sensitive personal information in your application or uploaded documents.
        </p>
      </div>

      <div className="mt-10">
        <button
          onClick={() => navigate(`/careers/${job.slug}/apply`)}
          data-testid="apply-cta-bottom"
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
        >
          Apply for This Position <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </article>
  );
}
